import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Layers2, Building2, Mountain, Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import { mapStyles } from "@/config/map";
import { MapControlsStore } from "@/components/Map/MapControls/MapControlsStore";
import type { MapStore } from "@/components/Map/stores/MapStore";

interface MapControlsProps {
	mapStore: MapStore<any>;
}

export const MapControls = observer(({ mapStore }: MapControlsProps) => {
	const mapControlsStore = useMemo(() => new MapControlsStore(), []);
	const [styleOpen, setStyleOpen] = useState(false);
	const [overlaysOpen, setOverlaysOpen] = useState(false);
	const overlays = mapStore.overlaysStore.catalog.data ?? [];

	useEffect(() => {
		if (!mapStore.isLoaded) return;
		const map = mapStore.mapRef.current?.getMap();
		if (!map) return;
		mapControlsStore.attach(map);
		return () => mapControlsStore.detach();
	}, [mapStore.isLoaded, mapControlsStore]);

	return (
		<div className="absolute left-[10px] bottom-[135px] z-10 inline-flex flex-col rounded-md bg-white shadow-[0_0_0_2px_#0000001a] overflow-hidden">
			<DropdownMenu open={styleOpen} onOpenChange={setStyleOpen}>
				<DropdownMenuTrigger asChild>
					<Button
						variant="ghost"
						size="icon"
						className="rounded-none bg-white hover:bg-white h-8 w-[30px]"
						onMouseEnter={() => setStyleOpen(true)}
						onMouseLeave={() => setStyleOpen(false)}
						aria-label="Background style"
					>
						<Layers2 className="h-4 w-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					side="right"
					align="start"
					onMouseEnter={() => setStyleOpen(true)}
					onMouseLeave={() => setStyleOpen(false)}
				>
					<DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">
						Background
					</DropdownMenuLabel>
					<DropdownMenuRadioGroup value={mapControlsStore.styleId} onValueChange={mapControlsStore.setStyle}>
						{mapStyles.map(style => (
							<DropdownMenuRadioItem
								key={style.id}
								value={style.id}
								className={cn(
									"flex items-center",
									mapControlsStore.styleId === style.id && "text-[#003BFF] font-semibold",
								)}
							>
								{style.label}
							</DropdownMenuRadioItem>
						))}
					</DropdownMenuRadioGroup>
				</DropdownMenuContent>
			</DropdownMenu>

			<Tooltip>
				<TooltipTrigger asChild>
					<Toggle
						size="sm"
						pressed={mapControlsStore.buildings}
						onPressedChange={mapControlsStore.toggleBuildings}
						className={cn(
							"rounded-none border-t border-input bg-white hover:bg-white h-8 px-1.5 min-w-6",
							mapControlsStore.buildings ? "text-[#003BFF]" : "text-gray-300",
						)}
						aria-label="Toggle 3D buildings"
					>
						<Building2 className="h-4 w-4" />
					</Toggle>
				</TooltipTrigger>
				<TooltipContent side="right">3D buildings</TooltipContent>
			</Tooltip>

			<Tooltip>
				<TooltipTrigger asChild>
					<Toggle
						size="sm"
						pressed={mapControlsStore.terrain}
						onPressedChange={mapControlsStore.toggleTerrain}
						className={cn(
							"rounded-none border-t border-input bg-white hover:bg-white h-8 px-1.5 min-w-6",
							mapControlsStore.terrain ? "text-[#003BFF]" : "text-gray-300",
						)}
						aria-label="Toggle Terrain"
					>
						<Mountain className="h-4 w-4" />
					</Toggle>
				</TooltipTrigger>
				<TooltipContent side="right">Terrain</TooltipContent>
			</Tooltip>

			<DropdownMenu open={overlaysOpen} onOpenChange={setOverlaysOpen}>
				<DropdownMenuTrigger asChild>
					<Button
						variant="ghost"
						size="icon"
						className="rounded-none border-t border-input bg-white hover:bg-white h-8 w-[30px]"
						onMouseEnter={() => setOverlaysOpen(true)}
						onMouseLeave={() => setOverlaysOpen(false)}
						aria-label="Overlays"
					>
						<Layers className="h-4 w-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					side="right"
					align="start"
					onMouseEnter={() => setOverlaysOpen(true)}
					onMouseLeave={() => setOverlaysOpen(false)}
				>
					<DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">
						Overlays
					</DropdownMenuLabel>
					{mapStore.overlaysStore.catalog.loading && (
						<div className="px-2 py-1.5 text-xs text-muted-foreground">Loading…</div>
					)}
					{!mapStore.overlaysStore.catalog.loading && overlays.length === 0 && (
						<div className="px-2 py-1.5 text-xs text-muted-foreground">No overlays available</div>
					)}
					<DropdownMenuRadioGroup>
						{overlays.map(overlayItem => (
							<DropdownMenuRadioItem
								key={overlayItem.overlayId}
								value={overlayItem.overlayId?.toString()}
								className="flex items-center"
								onSelect={event => {
									event.preventDefault();
									mapStore.overlaysStore.toggle(overlayItem.overlayId);
								}}
							>
								<Checkbox
									id={String(overlayItem.overlayId)}
									checked={mapStore.overlaysStore.isVisible(overlayItem.overlayId)}
									disabled={false}
									className="border-zinc-300 shadow-none h-5 w-5 mr-2 -ml-5"
								/>
								{overlayItem.name}
							</DropdownMenuRadioItem>
						))}
					</DropdownMenuRadioGroup>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
});
